import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FiCheck, FiInfo, FiPlus } from 'react-icons/fi';
import { graphqlRequest } from '../utils/graphqlRequest';
import { useAdminPageSearch } from '../utils/useAdminPageSearch';

const GET_SETTINGS = `query { getMyBillingPaymentSettings }`;
const START_SETUP = `mutation StartSetup($replace: String) { startBillingCardSetup(replacePaymentMethodId: $replace) }`;
const COMPLETE_SETUP = `mutation CompleteSetup($sessionId: String!) { completeBillingCardSetup(sessionId: $sessionId) }`;
const SET_DEFAULT = `mutation SetDefault($id: String!) { setDefaultBillingCard(paymentMethodId: $id) }`;
const REMOVE_CARD = `mutation RemoveCard($id: String!) { removeBillingCard(paymentMethodId: $id) }`;
const UPDATE_DETAILS = `mutation UpdateDetails($input: JSON!) { updateBillingDetails(input: $input) }`;
const SET_AUTO_RENEW = `mutation SetAutoRenew($on: Boolean!) { setBillingAutoRenew(autoRenew: $on) }`;

const regionName = (code) => {
  try {
    return code ? new Intl.DisplayNames(['en'], { type: 'region' }).of(code) : '';
  } catch {
    return code;
  }
};

const initials = (name) =>
  String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

export default function BillingPayment({ overview, formatDate }) {
  const { get, patch } = useAdminPageSearch();
  const setupSessionId = get('setup_session');
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const handledSession = useRef('');

  const run = async (key, query, variables, successMessage) => {
    setBusy(key);
    try {
      const res = await graphqlRequest(query, variables);
      const next = Object.values(res || {})[0];
      if (next) setSettings(next);
      if (successMessage) toast.success(successMessage);
      return next;
    } catch (e) {
      toast.error(e?.message || 'Something went wrong.');
      return null;
    } finally {
      setBusy('');
    }
  };

  useEffect(() => {
    // Returning from Stripe's card page: attach the new card, then clean the URL.
    if (setupSessionId) {
      if (handledSession.current === setupSessionId) return;
      handledSession.current = setupSessionId;
      graphqlRequest(COMPLETE_SETUP, { sessionId: setupSessionId })
        .then((res) => {
          setSettings(res?.completeBillingCardSetup);
          toast.success('Card saved and set as your default payment method.');
        })
        .catch((e) => {
          toast.error(e?.message || 'Card setup was not completed.');
          return graphqlRequest(GET_SETTINGS).then((res) => setSettings(res?.getMyBillingPaymentSettings));
        })
        .catch((e) => setError(e?.message || 'Failed to load payment settings.'))
        .finally(() => patch({ setup_session: '' }));
      return;
    }
    if (settings) return;
    graphqlRequest(GET_SETTINGS)
      .then((res) => setSettings(res?.getMyBillingPaymentSettings))
      .catch((e) => setError(e?.message || 'Failed to load payment settings.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupSessionId]);

  const openCardSetup = async (replaceId) => {
    setBusy(replaceId || 'add');
    try {
      const res = await graphqlRequest(START_SETUP, { replace: replaceId || null });
      window.location.assign(res.startBillingCardSetup.url);
    } catch (e) {
      toast.error(e?.message || 'Could not open the card page.');
      setBusy('');
    }
  };

  const removeCard = (pm) => {
    if (!window.confirm(`Remove ${pm.brand} ending in ${pm.last4}?`)) return;
    run(pm.id, REMOVE_CARD, { id: pm.id }, 'Card removed.');
  };

  const startEdit = () => {
    const d = settings.billingDetails;
    setForm({ ...d, address: { ...d.address, country: d.address.country || 'AU' } });
    setEditing(true);
  };

  const saveDetails = async (e) => {
    e.preventDefault();
    const saved = await run('details', UPDATE_DETAILS, { input: form }, 'Billing details saved.');
    if (saved) setEditing(false);
  };

  const toggleAutoRenew = () => {
    const on = !settings.autoRenew;
    if (
      !on &&
      !window.confirm(
        `Turn off auto-renew? Your subscription will end on ${formatDate(overview.trialEnd || overview.currentPeriodEnd)} and you will lose access after that date.`,
      )
    ) {
      return;
    }
    run('renew', SET_AUTO_RENEW, { on }, on ? 'Auto-renew turned on.' : 'Auto-renew turned off.');
  };

  if (error) return <p className="bs-text-danger">{error}</p>;
  if (!settings) return <p className="bs-muted">Loading payment settings…</p>;

  const { paymentMethods, billingDetails: d, contact } = settings;
  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setAddress = (key, value) => setForm((f) => ({ ...f, address: { ...f.address, [key]: value } }));
  const field = (label, value, onChange, props = {}) => (
    <label className="bs-field">
      <span>{label}</span>
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} {...props} />
    </label>
  );
  const cityLine = [d.address.city, d.address.state, d.address.postalCode].filter(Boolean).join(' ');

  return (
    <>
      <section className="bs-card bs-block">
        <div className="bs-plan-head">
          <div>
            <p className="bs-eyebrow">Payment methods</p>
            <h4 className="bs-staff-title">Saved payment methods</h4>
            <p className="bs-muted bs-small">Securely manage how you pay for your subscription.</p>
          </div>
          <button type="button" className="bs-btn bs-btn--primary bs-btn--icon" disabled={Boolean(busy)} onClick={() => openCardSetup()}>
            <FiPlus /> {busy === 'add' ? 'Opening…' : 'Add payment method'}
          </button>
        </div>

        {paymentMethods.length === 0 ? (
          <div className="bs-banner bs-banner--warn">
            <FiInfo />
            <div>
              <strong>No card on file</strong>
              <p>
                {settings.trialEnd
                  ? `Add a card before your free period ends on ${formatDate(settings.trialEnd)} to keep your access.`
                  : 'Add a card to keep your subscription active.'}
              </p>
            </div>
          </div>
        ) : (
          <ul className="bs-pm-list">
            {paymentMethods.map((pm) => (
              <li key={pm.id} className="bs-pm-row">
                <span className="bs-pm-brand">{pm.brand}</span>
                <div className="bs-pm-info">
                  <strong className="bs-pm-name">{pm.brand} ending in {pm.last4}</strong>
                  <span className="bs-muted bs-small">Expires {String(pm.expMonth).padStart(2, '0')}/{pm.expYear}</span>
                </div>
                <div className="bs-pm-badges">
                  {pm.isDefault && <span className="bs-badge bs-badge--grey">Default</span>}
                  {pm.verified && <span className="bs-badge bs-badge--green"><FiCheck /> Verified</span>}
                </div>
                <div className="bs-pm-actions">
                  {pm.isDefault ? (
                    <button type="button" className="bs-link" disabled={Boolean(busy)} onClick={() => openCardSetup(pm.id)}>
                      {busy === pm.id ? 'Opening…' : 'Update'}
                    </button>
                  ) : (
                    <button type="button" className="bs-link" disabled={Boolean(busy)} onClick={() => run(pm.id, SET_DEFAULT, { id: pm.id }, 'Default card updated.')}>
                      Make default
                    </button>
                  )}
                  <button type="button" className="bs-link bs-link--danger" disabled={Boolean(busy)} onClick={() => removeCard(pm)}>
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="bs-note">
          <FiInfo /> Payment details are encrypted and processed securely by Stripe. We never store full card details.
        </p>
      </section>

      <div className="bs-grid bs-grid--even">
        <section className="bs-card">
          <div className="bs-plan-head">
            <div>
              <p className="bs-eyebrow">Billing details</p>
              <h4 className="bs-staff-title">{d.businessName || 'Your business'}</h4>
            </div>
            {!editing && (
              <button type="button" className="bs-link" onClick={startEdit}>Edit</button>
            )}
          </div>

          {editing ? (
            <form className="bs-form" onSubmit={saveDetails}>
              {field('Business name', form.businessName, (v) => setField('businessName', v))}
              {field('Street address', form.address.line1, (v) => setAddress('line1', v))}
              {field('Address line 2', form.address.line2, (v) => setAddress('line2', v))}
              <div className="bs-form-row">
                {field('Suburb / City', form.address.city, (v) => setAddress('city', v))}
                {field('State', form.address.state, (v) => setAddress('state', v))}
              </div>
              <div className="bs-form-row">
                {field('Postcode', form.address.postalCode, (v) => setAddress('postalCode', v))}
                {field('Country code', form.address.country, (v) => setAddress('country', v.toUpperCase()), { maxLength: 2, placeholder: 'AU' })}
              </div>
              {field('ABN', form.abn, (v) => setField('abn', v), { inputMode: 'numeric', placeholder: '11 digits' })}
              <label className="bs-check-row">
                <input type="checkbox" checked={Boolean(form.gstRegistered)} onChange={(e) => setField('gstRegistered', e.target.checked)} />
                Registered for GST
              </label>
              <div className="bs-plan-actions">
                <button type="submit" className="bs-btn bs-btn--primary" disabled={busy === 'details'}>
                  {busy === 'details' ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="bs-btn" onClick={() => setEditing(false)}>Cancel</button>
              </div>
            </form>
          ) : (
            <dl className="bs-details">
              <div>
                <dt>Billing address</dt>
                <dd>
                  {d.address.line1 ? (
                    <>
                      {d.address.line1}
                      {d.address.line2 && <><br />{d.address.line2}</>}
                      <br />
                      {[cityLine, regionName(d.address.country)].filter(Boolean).join(', ')}
                    </>
                  ) : (
                    <span className="bs-muted">Not added</span>
                  )}
                </dd>
              </div>
              <div>
                <dt>ABN</dt>
                <dd>{d.abn ? d.abn.replace(/^(\d{2})(\d{3})(\d{3})(\d{3})$/, '$1 $2 $3 $4') : <span className="bs-muted">Not added</span>}</dd>
              </div>
              <div>
                <dt>GST status</dt>
                <dd>{d.gstRegistered ? 'Registered for GST' : 'Not registered for GST'}</dd>
              </div>
            </dl>
          )}
        </section>

        <section className="bs-card">
          <p className="bs-eyebrow">Billing contacts</p>
          <h4 className="bs-staff-title">Invoice recipients</h4>
          <div className="bs-contact">
            <span className="bs-avatar">{initials(contact.name || contact.email)}</span>
            <div>
              <strong>{contact.name || contact.email}</strong>
              <p className="bs-muted bs-small">{contact.email}</p>
            </div>
            <span className="bs-badge bs-badge--purple">Owner</span>
          </div>
          <p className="bs-muted bs-small">Invoices and payment receipts are sent to this email.</p>
        </section>
      </div>

      <section className="bs-card bs-renew">
        <div>
          <p className="bs-eyebrow">Subscription setting</p>
          <h4 className="bs-staff-title">Auto-renew</h4>
          <p className="bs-muted bs-small">
            {settings.autoRenew
              ? `Your ${overview.plan.name} plan will renew automatically${settings.trialEnd ? ' after your free access period' : ''}.`
              : `Your subscription will end on ${formatDate(overview.trialEnd || overview.currentPeriodEnd)}.`}
          </p>
        </div>
        <label className="bs-switch">
          <input
            type="checkbox"
            checked={settings.autoRenew}
            disabled={!settings.canChangeRenewal || busy === 'renew'}
            onChange={toggleAutoRenew}
            aria-label="Auto-renew"
          />
          <span className="bs-switch-track" aria-hidden="true" />
        </label>
      </section>
    </>
  );
}
